/* Modal เลือกวัตถุดิบ/สินค้า (หน้าตาเดียวกับ Master Process)
   props
     open, onClose
     title        หัว Modal
     fetchItems   () => Promise<rows[]>  แต่ละแถว { stcode, stname, typename, ... }
     typeLabel    map แปลง typename → ชื่อที่แสดง (ไม่ใส่ = ใช้ typename ตรง ๆ)
     extraColumns คอลัมน์เพิ่ม (เช่น คงเหลือ)
     onSelect     (row) => void   คลิกแถวแล้วปิด Modal */
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Card, Table, Input, Checkbox, Space, Spin, Typography, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { TagItemTypes } from "../../../badge-and-tag/ERP/";

const norm = (t) => (t ?? "").toString().trim().toLowerCase();

export default function ModalItemPicker({
  open, onClose, title = "เลือกวัตถุดิบ", fetchItems, typeLabel = {}, extraColumns = [], onSelect,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [types, setTypes] = useState([]);
  const [checked, setChecked] = useState([]);

  const label = (t) => typeLabel[t] || t || "-";

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setLoading(true);
    Promise.resolve(fetchItems())
      .then((data) => {
        const list = data || [];
        setRows(list);
        const ts = [...new Set(list.map((r) => r.typename || ""))];
        setTypes(ts);
        setChecked(ts);
      })
      .catch(() => message.error("โหลดรายการไม่สำเร็จ"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = useMemo(() => {
    const q = norm(query);
    const set = new Set(checked);
    return rows.filter(
      (r) => set.has(r.typename || "") &&
        (!q || norm(r.stcode).includes(q) || norm(r.stname).includes(q))
    );
  }, [rows, query, checked]);

  const choose = (r) => {
    onSelect?.(r);
    onClose?.();
  };
  const Pick = ({ r, children }) => (
    <Typography.Link className="text-select" onClick={() => choose(r)}>{children}</Typography.Link>
  );

  const columns = [
    { title: "รหัสสินค้า", dataIndex: "stcode", key: "stcode", width: "18%",
      sorter: (a, b) => (a.stcode || "").localeCompare(b.stcode || ""),
      render: (v, r) => <Pick r={r}>{v}</Pick> },
    { title: "ประเภทสินค้า", dataIndex: "typename", key: "typename", width: "15%",
      render: (v, r) => <Pick r={r}><TagItemTypes data={label(v)} /></Pick> },
    { title: "ชื่อสินค้า", dataIndex: "stname", key: "stname",
      sorter: (a, b) => (a.stname || "").localeCompare(b.stname || ""),
      render: (v, r) => <Pick r={r}>{v}</Pick> },
    ...extraColumns,
  ];

  return (
    <Modal open={open} title={title} onCancel={onClose} footer={null}
      maskClosable={false} style={{ top: 20 }} width={1200} destroyOnClose>
      <Spin spinning={loading}>
        <Space direction="vertical" size="middle" style={{ display: "flex" }}>
          <Card style={{ backgroundColor: "#f0f0f0" }}>
            <Space direction="vertical" style={{ display: "flex" }}>
              <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                suffix={<SearchOutlined />} placeholder="ค้นหาชื่อ หรือ รหัส" />
              {types.length > 1 && (
                <Space wrap>
                  <Checkbox
                    checked={checked.length === types.length}
                    indeterminate={checked.length > 0 && checked.length < types.length}
                    onChange={(e) => setChecked(e.target.checked ? types : [])}
                  >
                    <TagItemTypes data="Check All" />
                  </Checkbox>
                  <Checkbox.Group value={checked} onChange={setChecked}
                    options={types.map((t) => ({ value: t, label: <TagItemTypes data={label(t)} /> }))} />
                </Space>
              )}
            </Space>
          </Card>
          <Card style={{ minHeight: "60vh" }}>
            <Table bordered size="small" rowKey="stcode" dataSource={filtered} columns={columns}
              pagination={{
                defaultPageSize: 10, pageSizeOptions: [10, 25, 50, 100],
                showTotal: (t, r) => `${r[0]}-${r[1]} of ${t} items`,
              }} />
          </Card>
        </Space>
      </Spin>
    </Modal>
  );
}
